import { Injectable } from "@angular/core";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";

import { BlotterDefinitionLookupService } from "./BlotterDefinitionLookupService";
import { PrimaryLookupService } from "../../impl/PrimaryLookupService";
import { IBlotterDefinitionPromiseLookupService } from "../IBlotterDefinitionPromiseLookupService";
import { BaseLookupService } from "../../impl/BaseLookupService";
import { PromiseLookupService } from "../../impl/PromiseLookupService";

import { CubiqWorker } from "../../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class BlotterDefinitionPrimaryLookupService
  extends PrimaryLookupService<string, CubiqTS.BlotterDefinitionImpl>
  implements IBlotterDefinitionPromiseLookupService
{
  constructor(
    protected base: BlotterDefinitionLookupService,
    protected factory: ServiceMessageBrokerFactory,
    protected cubiqWorker: CubiqWorker,
  ) {
    super(base, factory, CubiqTS.BlotterDefinitionImpl, cubiqWorker, "BlotterDefinitionImpl");

    cubiqWorker.isAuth(`${this.constructor.name}`).then(
      () => {
        let lookupRequest: CubiqTS.BlotterDefinitionListRequestImpl =
          new CubiqTS.BlotterDefinitionListRequestImpl();

        this.cubiqWorker
          .postServiceRequest(lookupRequest)
          .then((report: CubiqTS.StandardReportImpl) => {
            this.base.prime(report.results);
            this._primed.resolve(true);
          })
          .catch((error: Error) => {
            console.debug("BlotterDefinition prime failed");
            this._primed.reject(error);
          });
      },
      (reason) => {
        console.debug("BlotterDefinitionPrimaryLookupService: Failed to prime: " + reason);

        this._primed.resolve(false);
      },
    );

    this.broker.registerMethod(
      "getBlotterDefinition",
      [PRIMITIVE],
      this.getBlotterDefinition.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getMyBlotterDefinitions",
      [],
      this.getMyBlotterDefinitions.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "deleteItemsByIds",
      [PRIMITIVE],
      this.deleteItemsByIds.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "createFilterItem",
      [this.VALUE],
      this.createFilterItem.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "updateFilterItem",
      [this.VALUE],
      this.updateFilterItem.bind(this),
      this.VALUE,
    );
  }

  getBlotterDefinition(code: string): Promise<CubiqTS.BlotterDefinitionImpl> {
    return new Promise<CubiqTS.BlotterDefinitionImpl>((res, rej) => {
      res(this.base.getBlotterDefinition(code));
    });
  }

  getMyBlotterDefinitions(): Promise<Array<CubiqTS.BlotterDefinitionImpl>> {
    return new Promise<Array<CubiqTS.BlotterDefinitionImpl>>((res, rej) => {
      res(this.base.getMyBlotterDefinitions(this.cubiqWorker.getUserId()));
    });
  }

  getBase(): BlotterDefinitionLookupService {
    return this.base;
  }

  createFilterItem(item: CubiqTS.BlotterDefinitionImpl): Promise<boolean> {
    return this.updateFilterItem(item);
  }

  updateFilterItem(item: CubiqTS.BlotterDefinitionImpl): Promise<boolean> {
    let blotterRequest: CubiqTS.BlotterDefinitionRequestImpl =
      new CubiqTS.BlotterDefinitionRequestImpl();
    blotterRequest.blotterId = item.blotterId;
    blotterRequest.blotterName = item.blotterName;
    blotterRequest.blotterType = item.blotterType;
    blotterRequest.queryGroup = item.queryGroup;
    return this.cubiqWorker.postServiceRequest(blotterRequest);
  }

  deleteItemsByIds(ids: number[]): Promise<boolean> {
    let blotterIds: Array<CubiqTS.Long> = new Array<CubiqTS.Long>();
    ids.forEach((id) => blotterIds.push(new CubiqTS.Long(id)));
    let blotterRequest: CubiqTS.BlotterDefinitionDeleteRequestImpl =
      new CubiqTS.BlotterDefinitionDeleteRequestImpl();
    blotterRequest.blotterIds = blotterIds;
    return this.cubiqWorker.postServiceRequest(blotterRequest);
  }
}
